// "Meus itens de recertificação" (`/me/access-reviews`; Parte 5.9 "Usuário: itens de recertificação"; REQ-AUTHZ-017;
// GIY-056): itens pendentes que cabem ao gestor (nomeado, escalonado ou elegível no escopo), com Manter/Revogar e nota —
// nunca o próprio acesso (`422 AUTHZ_SOD_VIOLATION` do servidor). Permissão-base `access.review.self`.
import {
  Button,
  NativeSelect,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@giyus/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ProblemAlert } from '../../../components';
import { AccountMeNav } from '../../../pages/Account';
import {
  type AccessReviewItem,
  useDecideAccessReview,
  useMyAccessReviewItems,
} from '../api/queries';
import { Field } from '../components/Fields';
import { StatusBadge } from '../components/StatusBadge';
import { TextDialog } from '../components/TextDialog';

export function MyAccessReviewsPage() {
  const { t } = useTranslation(['authz', 'common']);
  const items = useMyAccessReviewItems();
  const decide = useDecideAccessReview();
  const [deciding, setDeciding] = useState<AccessReviewItem | null>(null);
  const [decision, setDecision] = useState<'Keep' | 'Revoke'>('Keep');
  const [message, setMessage] = useState<string | null>(null);
  const decisionId = useId();

  return (
    <section className="flex flex-col gap-4" data-testid="my-access-reviews">
      <Link className="text-body text-muted-foreground underline-offset-2 hover:underline" to="/me">
        {t('authz:me.back')}
      </Link>
      <div>
        <h1 className="text-headline font-semibold tracking-tight">
          {t('authz:me.reviews.title')}
        </h1>
        <p className="text-muted-foreground">{t('authz:me.reviews.subtitle')}</p>
      </div>
      <AccountMeNav />
      {message && (
        <p role="status" className="text-body" data-testid="my-reviews-message">
          {message}
        </p>
      )}
      {items.isError ? (
        <ProblemAlert
          error={items.error}
          onRetry={() => {
            void items.refetch();
          }}
        />
      ) : items.isLoading || !items.data ? (
        <Skeleton className="h-32 w-full" />
      ) : items.data.length === 0 ? (
        <p className="text-body text-muted-foreground" data-testid="my-reviews-empty">
          {t('authz:me.reviews.empty')}
        </p>
      ) : (
        <Table data-testid="my-reviews-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('authz:accessReviews.items.columns.subject')}</TableHead>
              <TableHead>{t('authz:accessReviews.items.columns.role')}</TableHead>
              <TableHead>{t('authz:accessReviews.items.columns.scope')}</TableHead>
              <TableHead>{t('authz:accessReviews.items.columns.status')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.data.map((item) => (
              <TableRow key={item.id} data-testid={`my-review-${item.id}`}>
                <TableCell className="font-mono text-caption">{item.subjectUserId}</TableCell>
                <TableCell className="font-mono text-caption">{item.roleCode}</TableCell>
                <TableCell>
                  {t(`authz:scopeType.${item.scopeType}`, { defaultValue: item.scopeType })}
                  {item.scopeId ? ` · ${item.scopeId}` : ''}
                </TableCell>
                <TableCell>
                  <StatusBadge status={item.status} />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`my-review-decide-${item.id}`}
                    onClick={() => {
                      decide.reset();
                      setMessage(null);
                      setDecision('Keep');
                      setDeciding(item);
                    }}
                  >
                    {t('authz:me.reviews.actions.decide')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <TextDialog
        open={deciding !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDeciding(null);
          }
        }}
        title={t('authz:me.reviews.decideDialog.title')}
        description={t('authz:me.reviews.decideDialog.description', {
          role: deciding?.roleCode ?? '',
          user: deciding?.subjectUserId ?? '',
        })}
        textLabel={t('authz:me.reviews.decideDialog.note')}
        textRequired={false}
        confirmLabel={t('authz:me.reviews.actions.decide')}
        pending={decide.isPending}
        error={decide.error}
        testId="decide-dialog"
        onConfirm={(note) => {
          if (!deciding) {
            return;
          }
          decide
            .mutateAsync({ itemId: deciding.id, decision, note })
            .then(() => {
              setDeciding(null);
              setMessage(t(`authz:me.reviews.decided.${decision}`));
            })
            .catch(() => {
              // O erro fica no diálogo.
            });
        }}
      >
        <Field id={decisionId} label={t('authz:me.reviews.decideDialog.decision')}>
          <NativeSelect
            id={decisionId}
            value={decision}
            onChange={(event) => {
              setDecision(event.target.value as 'Keep' | 'Revoke');
            }}
          >
            <option value="Keep">{t('authz:status.Keep')}</option>
            <option value="Revoke">{t('authz:status.Revoke')}</option>
          </NativeSelect>
        </Field>
      </TextDialog>
    </section>
  );
}
